import type { ReactNode } from 'react';

import { Glyph } from '../chrome/Glyph.js';
import { LABEL } from '../../theme/type.js';

/**
 * The canvas's LAYERS control (design 356–370) — **lifted in Phase 6** (M5-D3).
 *
 * Top-right of whichever map is on screen, clear of the renderers' own zoom
 * column and LEGEND button in the two bottom corners. A button that opens a
 * 236 px panel of switches: a 12 px box that is filled when the layer is on, and
 * the layer's name. No captions — a filled box beside a name is self-evident,
 * and the same switches are in the canvas right-click menu for an operator who
 * never finds this button.
 *
 * ## What the lift cost, and what it did not
 *
 * The design puts one LAYERS control inside one canvas region and swaps only the
 * `x-import` under it (markup 343–370), so the second caller was never going to
 * need a different control — it needs a different *list*. So the component took
 * its table as a prop and became generic over the key: the theatre passes
 * `MAP_LAYER_LABELS` and five booleans, the orbital board passes
 * `SPACE_LAYER_LABELS` and four, and neither map can grow a switch this panel
 * does not draw. Nothing else changed.
 *
 * The one thing that did **not** generalise is the degradation notice. Terrain
 * is a live third-party tile service (Q23) and the orbital board has no basemap
 * at all — there is no `setTerrain` on `<orbital-map>` — so a `terrainAvailable`
 * prop here would be a permanent `true` on one of its two callers. It is a
 * `notice` slot instead, and the theatre passes the banner it owns.
 */
/**
 * One id, because one panel is open at a time: the theatre, orbital and cyber
 * boards are three tabs and never co-resident.
 */
const PANEL_ID = 'bs-map-layer-panel';

export function LayerPanel<K extends string>(props: {
  /** `[key, label]`, in the design's own order. The panel draws exactly these. */
  readonly labels: readonly (readonly [K, string])[];
  readonly layers: Readonly<Record<K, boolean>>;
  readonly open: boolean;
  readonly onToggleOpen: () => void;
  readonly onToggle: (layer: K) => void;
  /** Drawn above the button. The theatre's `TERRAIN UNAVAILABLE`, today. */
  readonly notice?: ReactNode;
}): JSX.Element {
  return (
    <div
      style={{
        position: 'absolute',
        top: 12,
        right: 12,
        zIndex: 'var(--z-map-panel)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 4,
      }}
    >
      {props.notice}
      <button
        type="button"
        data-testid="map-layers-toggle"
        aria-expanded={props.open}
        aria-controls={PANEL_ID}
        onClick={props.onToggleOpen}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 28,
          padding: '0 12px',
          background: 'var(--surface)',
          border: '1px solid var(--border-strong)',
          // Not amber. "A panel is open" is the loosest job amber had anywhere in the
          // console, and this button sits on the same screen as the one amber control
          // that locks a turn.
          color: props.open ? 'var(--accent)' : 'var(--text-2)',
          fontFamily: 'var(--font-ui)',
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          lineHeight: 1,
          cursor: 'pointer',
        }}
      >
        <Glyph name="layers" size={16} />
        LAYERS
      </button>
      {props.open && (
        <div
          id={PANEL_ID}
          data-testid="map-layer-panel"
          role="group"
          aria-label="Map layers"
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: 236,
            background: 'var(--surface)',
            border: '1px solid var(--border-strong)',
          }}
        >
          {props.labels.map(([key, label]) => {
            const on = props.layers[key];
            return (
              <button
                key={key}
                type="button"
                data-testid="map-layer-row"
                data-layer={key}
                aria-pressed={on}
                onClick={() => props.onToggle(key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  height: 32,
                  padding: '0 12px',
                  background: 'transparent',
                  border: 0,
                  borderBottom: '1px solid var(--rule-faint)',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    flex: 'none',
                    width: 12,
                    height: 12,
                    background: on ? 'var(--accent)' : 'transparent',
                    border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border-strong)'),
                  }}
                />
                <span
                  style={{
                    ...LABEL,
                    flex: 1,
                    // LABEL, because these are labels. In prose at 13px they were the
                    // only Barlow strings floating over a canvas whose every other
                    // word is condensed or mono.
                    color: on ? 'var(--text)' : 'var(--text-muted)',
                  }}
                >
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * The theatre's degradation banner, which travels with the switch it qualifies.
 *
 * A tile that did not arrive flips the basemap to vector — the renderer's own
 * behaviour, and Q23's accepted degradation *stated* rather than a map that
 * quietly looks different in the room than it did in rehearsal. It sits with the
 * layer control because that is where an operator goes to ask what is being
 * drawn.
 */
export function TerrainNotice(): JSX.Element {
  return (
    <span
      data-testid="map-terrain-degraded"
      style={{
        display: 'flex',
        alignItems: 'center',
        height: 28,
        padding: '0 10px',
        background: 'var(--surface)',
        border: '1px solid var(--warn)',
        fontFamily: 'var(--font-data)',
        fontSize: 10,
        letterSpacing: '.12em',
        whiteSpace: 'nowrap',
        color: 'var(--warn)',
      }}
    >
      TERRAIN UNAVAILABLE — VECTOR
    </span>
  );
}
